import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import RelationshipForm from './RelationshipForm'
import EditRelationshipForm from './EditRelationshipForm'
import {
  getBaseRelationshipType,
  getOppositeRelationship,
  getRelationshipBasedOnSex,
} from '../utils/relationshipMappings'

const {
  FiX,
  FiPlus,
  FiTrash2,
  FiEdit,
  FiHeart,
  FiHome,
  FiBriefcase,
  FiUsers,
  FiZap,
  FiInfo,
  FiAlertTriangle,
} = FiIcons

const RelationshipModal = ({
  person,
  people,
  allRelationships,
  onClose,
  onAddPerson,
  onPersonUpdate,
  onAddRelationships,
  onUpdateRelationship,
  onDeleteRelationship,
  onCleanupDuplicates,
}) => {
  const [showForm, setShowForm] = useState(false)
  const [editingRelationship, setEditingRelationship] = useState(null)
  const [relationships, setRelationships] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [historyHandled, setHistoryHandled] = useState(false)
  const [smartRelationshipResults, setSmartRelationshipResults] = useState(null)
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false)
  const [cleanupInProgress, setCleanupInProgress] = useState(false)
  const [relationshipToDelete, setRelationshipToDelete] = useState(null)

  useEffect(() => {
    if (historyHandled) return
    const handleBackButton = (event) => {
      if (relationshipToDelete) {
        event.preventDefault()
        setRelationshipToDelete(null)
        return
      }
      if (showForm) {
        event.preventDefault()
        setShowForm(false)
        return
      }
      if (editingRelationship) {
        event.preventDefault()
        setEditingRelationship(null)
        return
      }
      if (onClose) onClose()
    }
    window.addEventListener('popstate', handleBackButton)
    if (!historyHandled) {
      window.history.pushState({ action: 'relationshipModal' }, '')
      setHistoryHandled(true)
    }
    return () => {
      window.removeEventListener('popstate', handleBackButton)
    }
  }, [onClose, showForm, editingRelationship, historyHandled, relationshipToDelete])

  useEffect(() => {
    if (person && allRelationships) {
      setLoading(true)
      setError(null)
      const personRels = allRelationships.filter(
        (rel) => rel.person_a_id === person.id || rel.person_b_id === person.id
      )

      const seenPairs = new Map()
      let hasDuplicates = false
      personRels.forEach((rel) => {
        const personA = rel.person_a_id
        const personB = rel.person_b_id
        const pairKey = [personA, personB].sort().join('_')
        if (!seenPairs.has(pairKey)) {
          seenPairs.set(pairKey, true)
        } else {
          hasDuplicates = true
        }
      })
      setShowDuplicateWarning(hasDuplicates)
      setRelationships(personRels)
      setLoading(false)
    }
  }, [person, allRelationships])

  const handleCleanupDuplicates = async () => {
    try {
      setCleanupInProgress(true)
      await onCleanupDuplicates()
    } catch (err) {
      console.error('Error cleaning up duplicates:', err)
      setError(`Failed to clean up duplicates: ${err.message}`)
    } finally {
      setCleanupInProgress(false)
    }
  }

  const handleAddRelationships = async (relationshipData) => {
    try {
      setLoading(true)
      setError(null)
      setSmartRelationshipResults(null)
      const result = await onAddRelationships(person.id, relationshipData)
      if (result && result.count > 0) {
        setSmartRelationshipResults(result)
        setTimeout(() => {
          setSmartRelationshipResults(null)
        }, 8000)
      }
      setShowForm(false)
    } catch (err) {
      console.error('Error adding relationships:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleEditRelationship = async (relationshipData) => {
    try {
      setLoading(true)
      setError(null)
      await onUpdateRelationship(
        editingRelationship.id,
        relationshipData,
        person.id
      )
      setEditingRelationship(null)
    } catch (err) {
      console.error('Error updating relationship:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteRelationshipClick = (displayRel) => {
    setRelationshipToDelete({
      id: displayRel.id,
      name: displayRel.name,
      type: displayRel.type,
    })
  }

  const confirmDeleteRelationship = async () => {
    if (relationshipToDelete) {
      try {
        setError(null)
        await onDeleteRelationship(relationshipToDelete.id)
        setRelationshipToDelete(null)
      } catch (err) {
        console.error('Error deleting relationship:', err)
        setError(err.message || 'Failed to delete relationship.')
        setRelationshipToDelete(null)
      }
    }
  }

  const cancelDeleteRelationship = () => {
    setRelationshipToDelete(null)
  }

  const getRelationshipIcon = (type) => {
    const familyTypes = [
      'father', 'mother', 'son', 'daughter', 'brother', 'sister',
      'grandfather', 'grandmother', 'grandson', 'granddaughter', 'uncle',
      'aunt', 'nephew', 'niece', 'husband', 'wife', 'ex-husband', 'ex-wife',
      'great-grandfather', 'great-grandmother', 'great-grandson', 'great-granddaughter',
      'great-great-grandfather', 'great-great-grandmother', 'great-uncle',
      'great-aunt', 'grandnephew', 'grandniece', 'cousin', 'male cousin',
      'female cousin', 'second_cousin', 'male 2nd cousin', 'female 2nd cousin',
      'third_cousin', 'male 3rd cousin', 'female 3rd cousin',
    ]
    const friendTypes = ['friend']
    const workTypes = ['colleague', 'boss', 'employee']

    if (familyTypes.includes(type)) return FiHome
    if (friendTypes.includes(type)) return FiHeart
    if (workTypes.includes(type)) return FiBriefcase
    return FiUsers
  }

  const deriveRelationshipLabel = (rel, isPersonA, otherPersonSex, fallback) => {
    const normalizedSex = String(otherPersonSex || 'other').trim().toLowerCase()
    if (isPersonA) {
      const baseTypeAToB = getBaseRelationshipType(rel.relationship_type)
      if (baseTypeAToB) {
        const expectedBaseForBToA = getOppositeRelationship(baseTypeAToB, null)
        if (expectedBaseForBToA) {
          return getRelationshipBasedOnSex(expectedBaseForBToA, normalizedSex) || fallback
        }
      }
    } else {
      const baseTypeBToA = getBaseRelationshipType(rel.relationship_type_b)
      if (baseTypeBToA) {
        const expectedBaseForAToB = getOppositeRelationship(baseTypeBToA, null)
        if (expectedBaseForAToB) {
          return getRelationshipBasedOnSex(expectedBaseForAToB, normalizedSex) || fallback
        }
      }
    }
    return fallback
  }

  const getDisplayRelationships = () => {
    return relationships.map((rel) => {
      const isPersonA = rel.person_a_id === person.id
      const otherPersonId = isPersonA ? rel.person_b_id : rel.person_a_id
      const otherPersonFromRel = isPersonA ? rel.person_b : rel.person_a
      const otherPerson = otherPersonFromRel || people.find((p) => p.id === otherPersonId)
      const otherPersonName = otherPerson?.name || 'Unknown'
      const otherPersonSex = otherPerson?.sex || (isPersonA ? rel.person_b?.sex : rel.person_a?.sex)
      const fallbackRelationshipType = isPersonA ? rel.relationship_type_b || rel.relationship_type : rel.relationship_type || rel.relationship_type_b
      const relationshipType = deriveRelationshipLabel(rel, isPersonA, otherPersonSex, fallbackRelationshipType || 'Unknown')

      return {
        id: rel.id,
        name: otherPersonName,
        type: relationshipType,
        icon: getRelationshipIcon(relationshipType),
        personId: otherPersonId,
        fullRelationship: rel,
        isPersonA,
      }
    })
  }

  const displayRelationships = getDisplayRelationships()

  const handleAddNewPerson = async (name) => {
    if (!onAddPerson) return null
    try {
      const result = await onAddPerson(name)
      return result
    } catch (error) {
      console.error('Failed to add person from relationship modal:', error)
      throw error
    }
  }

  const handleClose = () => {
    if (onClose) onClose()
  }

  const handleEditClick = (displayRel) => {
    setEditingRelationship({
      id: displayRel.id,
      person_a_id: displayRel.fullRelationship.person_a_id,
      person_b_id: displayRel.fullRelationship.person_b_id,
      currentPersonId: person.id,
      otherPersonId: displayRel.personId,
      otherPersonName: displayRel.name,
      currentRelationshipType: displayRel.type,
      isPersonA: displayRel.isPersonA,
    })
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4 fixed-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose()
      }}
    >
      <AnimatePresence mode="wait">
        {relationshipToDelete ? (
          <motion.div
            key="delete-confirmation"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiTrash2} className="text-2xl text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-800">Delete Relationship</h2>
              <p className="text-gray-600 mt-2">
                Are you sure you want to delete the relationship with{' '}
                <strong>{relationshipToDelete.name}</strong> ({relationshipToDelete.type})?
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={cancelDeleteRelationship}
                className="flex-1 bg-gray-200 text-gray-800 py-3 px-4 rounded-lg hover:bg-gray-300 transition-colors flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiX} />
                <span>Cancel</span>
              </button>
              <button
                onClick={confirmDeleteRelationship}
                className="flex-1 bg-red-600 text-white py-3 px-4 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiTrash2} />
                <span>Delete</span>
              </button>
            </div>
          </motion.div>
        ) : editingRelationship ? (
          <EditRelationshipForm
            key="edit-form"
            relationship={editingRelationship}
            person={person}
            people={people}
            onSubmit={handleEditRelationship}
            onCancel={() => {
              setEditingRelationship(null)
            }}
          />
        ) : showForm ? (
          <RelationshipForm
            key="add-form"
            person={person}
            people={people}
            existingRelationships={relationships}
            allRelationships={allRelationships}
            onSubmit={handleAddRelationships}
            onCancel={() => {
              setShowForm(false)
            }}
            onAddNewPerson={handleAddNewPerson}
          />
        ) : (
          <motion.div
            key="modal"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="bg-white rounded-lg shadow-lg p-4 sm:p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-800">
                {person.name}'s Relationships
              </h2>
              <button
                onClick={handleClose}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                <SafeIcon icon={FiX} />
              </button>
            </div>

            {showDuplicateWarning && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiAlertTriangle} className="text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-yellow-800 mb-2">
                      Duplicate Relationships Detected
                    </h3>
                    <p className="text-sm text-yellow-700 mb-2">
                      This person has duplicate relationship connections. This may cause issues with family connections.
                    </p>
                    <button
                      onClick={handleCleanupDuplicates}
                      disabled={cleanupInProgress}
                      className="px-3 py-1 bg-yellow-600 text-white rounded-md text-sm hover:bg-yellow-700 disabled:opacity-50 flex items-center space-x-1"
                    >
                      {cleanupInProgress ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Cleaning up...</span>
                        </>
                      ) : (
                        <>
                          <SafeIcon icon={FiZap} className="mr-1" />
                          <span>Fix Duplicates</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <AnimatePresence>
              {smartRelationshipResults && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg"
                >
                  <div className="flex items-start space-x-3">
                    <SafeIcon icon={FiZap} className="text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="font-medium text-green-800 mb-2">
                        Smart Family Connections Added! ✨
                      </h3>
                      <p className="text-sm text-green-700 mb-2">
                        Added {smartRelationshipResults.count} automatic relationship{smartRelationshipResults.count !== 1 ? 's' : ''}:
                      </p>
                      <ul className="text-sm text-green-700 space-y-1">
                        {smartRelationshipResults.explanations.map(
                          (explanation, index) => (
                            <li key={index} className="flex items-start space-x-1">
                              <span className="text-green-500 mt-0.5">•</span>
                              <span>{explanation}</span>
                            </li>
                          )
                        )}
                      </ul>
                      <button
                        onClick={() => setSmartRelationshipResults(null)}
                        className="mt-2 text-xs text-green-600 hover:text-green-800"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading relationships...</p>
              </div>
            ) : (
              <>
                {displayRelationships.length > 0 ? (
                  <div className="space-y-3 mb-6">
                    {displayRelationships.map((rel) => (
                      <div
                        key={rel.id}
                        className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="bg-blue-100 p-2 rounded-full">
                            <SafeIcon icon={rel.icon} className="text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium">{rel.name}</p>
                            <p className="text-sm text-gray-500 capitalize">
                              {rel.type}
                            </p>
                          </div>
                        </div>
                        <div className="flex space-x-1">
                          <button
                            onClick={() => handleEditClick(rel)}
                            className="text-green-500 hover:text-green-700 p-1"
                            title="Edit relationship"
                          >
                            <SafeIcon icon={FiEdit} />
                          </button>
                          <button
                            onClick={() => handleDeleteRelationshipClick(rel)}
                            className="text-red-500 hover:text-red-700 p-1"
                            title="Delete relationship"
                          >
                            <SafeIcon icon={FiTrash2} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 mb-6">
                    <SafeIcon icon={FiUsers} className="text-4xl text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-600">
                      No relationships recorded yet.
                    </p>
                  </div>
                )}
                <button
                  onClick={() => {
                    setShowForm(true)
                  }}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center"
                >
                  <SafeIcon icon={FiPlus} className="mr-2" /> Add New Relationship
                </button>
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <SafeIcon icon={FiInfo} className="text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">
                        Smart Family Connections
                      </p>
                      <p>
                        When you add family relationships (spouse, parent, child, sibling), the app automatically creates related connections. For example, adding a spouse will make your children their children too!
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}
export default RelationshipModal